import React, { FC, useRef, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, TextField, Tooltip, FormHelperText } from 'drift-ui';
import styles from './BusinessInformation.scss';
import {
  useSectionFormHelpers,
  phoneRegex,
  taxIdRegex,
  STATE_OPTIONS,
  zipRegex,
  phoneFormatter,
  maskTaxId,
  DO_APPLICATION_TYPE,
  BankingApplicationDocument,
  generalTextRegex,
  getPrefillBannerSectionStatus,
  isPrefillFieldLocked,
} from 'client-experience-toolbox';
import { sendDOFirebaseEvent } from 'utils/utils';
import TooltipIcon from '../../assets/images/info.svg';
import { useTranslation } from 'react-i18next';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import { ContinueButton } from '../../components/ContinueButton';
import { useUpdateApplication } from 'utils/useUpdateApplication';
import { useApplication } from 'hooks/useApplication';
import baseStyles from './BaseFormStyles.scss';
import cx from 'classnames';
import { PrefillBanner } from 'components/PrefillBanner';

export const BusinessInformation: FC = () => {
  const { application } = useApplication(DO_APPLICATION_TYPE.BANKING);
  const bankingApplication = application as BankingApplicationDocument;
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [isPatching, setIsPatching] = useState(false);
  const { patchApplication, patchFullApplication } = useFormNetworkCalls(DO_APPLICATION_TYPE.BANKING);
  const [editingTaxId, setEditingTaxId] = useState(false);
  const updateApplication = useUpdateApplication(DO_APPLICATION_TYPE.BANKING);
  const prefillBannerStatus = getPrefillBannerSectionStatus(bankingApplication?.prefilledFields, 'businessInformation');
  const isFieldDisabled = (field: string) => isPrefillFieldLocked(bankingApplication?.prefilledFields || [], field);

  const {
    textHandlerGenerator,
    selectDropdownHandlerGenerator,
    numberHandlerGenerator,
    clearValidation,
    regexValidatorGenerator,
    presenceValidatorGenerator,
    handleInputFocus,
  } = useSectionFormHelpers({
    errorSetter: setErrors,
    patchApplication,
    updateApplication,
    application,
    fireBaseLogEvent: sendDOFirebaseEvent,
  });
  const applicationData = application?.applicationData as BankingApplicationDocument['applicationData'];
  const formRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const taxIdValue = applicationData?.company?.irsTaxIdOrEin ?? '';

  const hasTaxIdError = Boolean(errors['applicationData.company.irsTaxIdOrEin']);
  let displayTaxId: string;
  if (editingTaxId || hasTaxIdError) {
    displayTaxId = taxIdValue;
  } else if (applicationData?.company?.irsTaxIdOrEinDisplay?.length) {
    displayTaxId = maskTaxId(applicationData.company.irsTaxIdOrEinDisplay, true);
  } else {
    displayTaxId = maskTaxId(taxIdValue);
  }

  const fieldValidators: { [key: string]: () => boolean } = {
    companyName: regexValidatorGenerator({
      key: 'applicationData.company.name',
      regex: generalTextRegex,
      errorText: t('Required'),
    }),
    doingBusinessAs: regexValidatorGenerator({
      key: 'applicationData.company.doingBusinessAs',
      regex: generalTextRegex,
      errorText: t('digital_onboarding.common_section.invalid_company'),
      optional: true,
    }),
    irsTaxIdOrEin: regexValidatorGenerator({
      key: `applicationData.company.irsTaxIdOrEin`,
      regex: taxIdRegex,
      errorText: t('digital_onboarding.banking.owner_info.invalid_ssn'),
      skip: Boolean(applicationData?.company?.irsTaxIdOrEinDisplay),
      skipPatch: true,
    }),
    businessStreetAddress: regexValidatorGenerator({
      key: 'applicationData.company.address.street',
      regex: generalTextRegex,
      errorText: t('Required'),
    }),
    secondaryStreetAddress: regexValidatorGenerator({
      key: 'applicationData.company.address.secondaryStreet',
      regex: generalTextRegex,
      errorText: t('digital_onboarding.common_section.invalid_street'),
      optional: true,
    }),
    tertiaryStreetAddress: regexValidatorGenerator({
      key: 'applicationData.company.address.tertiaryStreet',
      regex: generalTextRegex,
      errorText: t('digital_onboarding.common_section.invalid_street'),
      optional: true,
    }),
    city: regexValidatorGenerator({
      key: 'applicationData.company.address.city',
      regex: generalTextRegex,
      errorText: t('Required'),
    }),
    state: presenceValidatorGenerator({
      key: 'applicationData.company.address.state',
      errorText: t('Required'),
    }),
    zipCode: regexValidatorGenerator({
      key: 'applicationData.company.address.zip',
      regex: zipRegex,
      errorText: t('digital_onboarding.common_section.invalid_zip'),
    }),
    businessPhone: regexValidatorGenerator({
      key: 'applicationData.company.phone',
      regex: phoneRegex,
      errorText: t('digital_onboarding.banking.owner_info.invalid_phone'),
      optional: true,
    }),
  };

  const scrollToError = (selector = '.Mui-error') => {
    const errorInput = formRef.current?.querySelector(selector) as HTMLElement;
    if (errorInput) {
      errorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      errorInput.focus({ preventScroll: true });
    }
  };

  const handleContinueClick = async () => {
    const isValid = await checkFormValidation();

    if (!isValid) {
      scrollToError();
    }
  };

  const checkFormValidation = async () => {
    const results = Object.values(fieldValidators).map((validator, index) => ({
      index,
      isValid: validator(),
    }));
    const firstInvalid = results.find(({ isValid }) => !isValid);

    if (firstInvalid) {
      return false;
    } else {
      await patchFullApplication(setIsPatching);
      return true;
    }
  };

  const onChangeTaxIdField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let value = event.target.value;
    if (!editingTaxId && !hasTaxIdError) {
      const isAppend = value.startsWith(displayTaxId);
      const isBackspace = !isAppend && value.startsWith(displayTaxId.slice(0, -1));

      if (isAppend) value = value.slice(displayTaxId.length);
      else if (isBackspace) value = '';
    }

    value = value.replace(/[^0-9]/g, '');

    if (value.length > 9) value = value.slice(0, 9);

    updateApplication('applicationData.company.irsTaxIdOrEin', value);
    setEditingTaxId(true);
    clearValidation('applicationData.company.irsTaxIdOrEin');
    updateApplication('applicationData.company.irsTaxIdOrEinDisplay', '');
  };

  const onBlurTaxIdField = async () => {
    const isValid = fieldValidators.irsTaxIdOrEin();
    setEditingTaxId(false);
    updateApplication('applicationData.company.irsTaxIdOrEin', taxIdValue);

    if (isValid && patchApplication) {
      updateApplication(`applicationData.company.irsTaxIdOrEinDisplay`, taxIdValue.slice(-4));
      await patchApplication('applicationData.company.irsTaxIdOrEin', taxIdValue);
    }
  };

  return (
    <div className={cx(baseStyles.formContainer, styles.businessInformationContainer)}>
      <div ref={formRef} className={baseStyles.formFieldContainer}>
        <div>
          <p className={styles.title}>{t('digital_onboarding.banking.business_info.description')}</p>
          {prefillBannerStatus !== 'none' && <PrefillBanner hasLockedFields={prefillBannerStatus === 'locked'} />}
        </div>
        <div className={baseStyles.groupedFields}>
          <TextField
            label={t('digital_onboarding.banking.business_info.label_business_name')}
            value={applicationData?.company?.name ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.name`, clearValidation)}
            onBlur={fieldValidators.companyName}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.name']}
            error={!!errors['applicationData.company.name']}
            slotProps={{
              htmlInput: {
                maxLength: 500,
                'data-nid-target': 'business-info-business-name',
              } as React.InputHTMLAttributes<HTMLInputElement>,
            }}
            disabled={isFieldDisabled(`applicationData.company.name`)}
          />
          <TextField
            label={t('digital_onboarding.banking.business_info.label_doing_business_as')}
            value={applicationData?.company?.doingBusinessAs ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.doingBusinessAs`, clearValidation)}
            onBlur={fieldValidators.doingBusinessAs}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.doingBusinessAs']}
            error={!!errors['applicationData.company.doingBusinessAs']}
            slotProps={{
              htmlInput: {
                maxLength: 500,
                'data-nid-target': 'business-info-doing-business-as',
              } as React.InputHTMLAttributes<HTMLInputElement>,
            }}
            disabled={isFieldDisabled(`applicationData.company.doingBusinessAs`)}
          />
        </div>
        <div className={baseStyles.singleField}>
          <div className={baseStyles.tooltipField}>
            <TextField
              label={t('digital_onboarding.banking.business_info.label_employer_identification_number')}
              onFocus={handleInputFocus}
              value={displayTaxId}
              onChange={onChangeTaxIdField}
              onBlur={onBlurTaxIdField}
              helperText={errors['applicationData.company.irsTaxIdOrEin']}
              error={hasTaxIdError}
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  'data-nid-target': 'business-info-ein',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
            />
            <div className={baseStyles.tooltipContainer}>
              <Tooltip
                title={t('digital_onboarding.banking.business_info.ein_tooltip')}
                placement="top"
                enterTouchDelay={0}
                leaveTouchDelay={8000}
                slotProps={{
                  tooltip: {
                    className: baseStyles.tooltip,
                  },
                }}
              >
                <img
                  src={TooltipIcon}
                  alt="Information Icon"
                  width={24}
                  height={24}
                  className={baseStyles.tooltipIcon}
                  tabIndex={0}
                />
              </Tooltip>
            </div>
          </div>
        </div>
        <p className={styles.invalidAddresses}>
          {t('digital_onboarding.banking.business_info.label_invalid_addresses')}
        </p>
        <div className={baseStyles.groupedFields}>
          <TextField
            label={t('digital_onboarding.banking.business_info.label_street')}
            value={applicationData?.company?.address?.street ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.address.street`, clearValidation)}
            onBlur={fieldValidators.businessStreetAddress}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.address.street']}
            error={!!errors['applicationData.company.address.street']}
            slotProps={{
              htmlInput: {
                maxLength: 500,
                'data-nid-target': 'business-info-street',
              } as React.InputHTMLAttributes<HTMLInputElement>,
            }}
            disabled={isFieldDisabled(`applicationData.company.address.street`)}
          />
          <TextField
            label={t('digital_onboarding.banking.business_info.label_secondary_street')}
            value={applicationData?.company?.address?.secondaryStreet ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.address.secondaryStreet`, clearValidation)}
            onBlur={fieldValidators.businessSecondaryStreetAddress}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.address.secondaryStreet']}
            error={!!errors['applicationData.company.address.secondaryStreet']}
            slotProps={{
              htmlInput: {
                maxLength: 500,
                'data-nid-target': 'business-info-secondary-street',
              } as React.InputHTMLAttributes<HTMLInputElement>,
            }}
            disabled={isFieldDisabled(`applicationData.company.address.secondaryStreet`)}
          />
        </div>
        <div className={baseStyles.groupedFields}>
          <TextField
            label={t('digital_onboarding.banking.business_info.label_tertiary_street')}
            value={applicationData?.company?.address?.tertiaryStreet ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.address.tertiaryStreet`, clearValidation)}
            onBlur={fieldValidators.businessTertiaryStreetAddress}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.address.tertiaryStreet']}
            error={!!errors['applicationData.company.address.tertiaryStreet']}
            slotProps={{
              htmlInput: {
                maxLength: 500,
                'data-nid-target': 'business-info-tertiary-street',
              } as React.InputHTMLAttributes<HTMLInputElement>,
            }}
            disabled={isFieldDisabled(`applicationData.company.address.tertiaryStreet`)}
          />
          <TextField
            label={t('city')}
            value={applicationData?.company?.address?.city ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.address.city`, clearValidation)}
            onBlur={fieldValidators.city}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.address.city']}
            error={!!errors['applicationData.company.address.city']}
            slotProps={{
              htmlInput: {
                maxLength: 500,
                'data-nid-target': 'business-info-city',
              } as React.InputHTMLAttributes<HTMLInputElement>,
            }}
            disabled={isFieldDisabled(`applicationData.company.address.city`)}
          />
        </div>
        <div className={baseStyles.groupedFields}>
          <FormControl fullWidth error={!!errors['applicationData.company.address.state']}>
            <InputLabel id="state-label">{t('state')}</InputLabel>
            <Select
              labelId="state-label"
              label={t('state')}
              value={applicationData?.company?.address?.state ?? ''}
              onChange={selectDropdownHandlerGenerator(`applicationData.company.address.state`, clearValidation)}
              onFocus={handleInputFocus}
              fullWidth
              slotProps={{
                input: { 'data-nid-target': 'business-info-state' } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
              disabled={isFieldDisabled(`applicationData.company.address.state`)}
            >
              {STATE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors[`applicationData.company.address.state`] && (
              <FormHelperText>{errors[`applicationData.company.address.state`]}</FormHelperText>
            )}
          </FormControl>
          <div className={baseStyles.smallFields}>
            <TextField
              label={t('zip_code')}
              value={applicationData?.company?.address?.zip ?? ''}
              onChange={textHandlerGenerator(`applicationData.company.address.zip`, clearValidation)}
              onBlur={fieldValidators.zipCode}
              onFocus={handleInputFocus}
              helperText={errors['applicationData.company.address.zip']}
              error={!!errors['applicationData.company.address.zip']}
              slotProps={{
                htmlInput: {
                  maxLength: 5,
                  inputMode: 'numeric',
                  'data-nid-target': 'business-info-zip',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
              disabled={isFieldDisabled(`applicationData.company.address.zip`)}
            />
            <div>
              <TextField
                label={t('digital_onboarding.common_section.label_country')}
                value={'USA'}
                disabled
                slotProps={{
                  htmlInput: {
                    'data-nid-target': 'business-info-country',
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
              <p className={styles.subText}>{t('digital_onboarding.banking.owner_info.country_helper_text')}</p>
            </div>
          </div>
        </div>
        <div className={baseStyles.singleField}>
          <TextField
            label={t('digital_onboarding.banking.business_info.label_business_phone')}
            value={phoneFormatter(applicationData?.company?.phone ?? '')}
            onChange={numberHandlerGenerator(`applicationData.company.phone`, clearValidation)}
            onBlur={fieldValidators.businessPhone}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.phone']}
            error={!!errors['applicationData.company.phone']}
            slotProps={{
              htmlInput: {
                maxLength: 16,
                inputMode: 'numeric',
                'data-nid-target': 'business-info-phone',
              } as React.InputHTMLAttributes<HTMLInputElement>,
            }}
            disabled={isFieldDisabled(`applicationData.company.phone`)}
          />
        </div>
      </div>
      <ContinueButton
        buttonStyles={baseStyles.buttonStyle}
        title={t('digital_onboarding.next')}
        handleContinueClick={handleContinueClick}
        isPatching={isPatching}
      />
    </div>
  );
};
